import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Save, Trash2, Edit2, ArrowLeft, X, Check, Eye, EyeOff, Building2, Link as LinkIcon } from 'lucide-react';
import { listCompaniesAdmin, createCompany, updateCompany, deleteCompany, adminVerify, DEFAULT_BASE_URL } from '../../services/api';
import toast from 'react-hot-toast';

const emptyForm = {
  name: '',
  tagline: '',
  websiteUrl: '',
  logoUrl: '',
  displayOrder: 0,
  isActive: true
};

const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = ['image/png', 'image/svg+xml'];

const isAllowedLogoUrl = (url) => {
  if (!url) return true;
  const lower = url.toLowerCase();
  if (lower.startsWith('data:image/png') || lower.startsWith('data:image/svg+xml')) return true;
  return /\.(png|svg)(\?.*)?$/.test(lower);
};

const getApiOrigin = () => {
  try {
    const apiBase = DEFAULT_BASE_URL.replace(/\/$/, '');
    const parsed = new URL(apiBase);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return window.location.origin;
  }
};

const getImageSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/')) return `${getApiOrigin()}${url}`;
  return url;
};

const AdminCompaniesPage = () => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imageInputMode, setImageInputMode] = useState('url');
  const [imageFile, setImageFile] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState('');
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [filters, setFilters] = useState({
    name: '',
    status: 'all'
  });
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await refresh();
      } catch (e) {
        navigate('/admin');
      }
    };
    init();
  }, [navigate]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl('');
      return undefined;
    }

    const preview = URL.createObjectURL(imageFile);
    setImagePreviewUrl(preview);

    return () => URL.revokeObjectURL(preview);
  }, [imageFile]);

  function applyFilters(data) {
    let filtered = [...data];

    if (filters.name) {
      filtered = filtered.filter(item =>
        (item.name || '').toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters.status === 'active') {
      filtered = filtered.filter(item => item.isActive !== false);
    } else if (filters.status === 'inactive') {
      filtered = filtered.filter(item => item.isActive === false);
    }

    setItems(filtered);
  }

  async function refresh() {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const companies = await listCompaniesAdmin();
      const sorted = (companies || []).sort((a, b) => {
        const orderA = Number(a.displayOrder || 0);
        const orderB = Number(b.displayOrder || 0);
        if (orderA !== orderB) return orderA - orderB;
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      setAllItems(sorted);
      applyFilters(sorted);
    } catch (e) {
      const errorMsg = e.message || 'Failed to load companies';
      setError(errorMsg);
      toast.error(errorMsg);
      setAllItems([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    applyFilters(allItems);
  }, [filters, allItems]);

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setImageFile(null);
      return;
    }
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast.error('Please upload a PNG or SVG logo only');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error('Image size must be 1MB or less');
      event.target.value = '';
      return;
    }
    setImageFile(file);
    setForm(prev => ({ ...prev, logoUrl: '' }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name) {
      setError('Please provide a company name');
      return;
    }
    if (imageInputMode === 'url' && form.logoUrl && !isAllowedLogoUrl(form.logoUrl)) {
      setError('Logo URL must be a PNG or SVG image');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name,
        tagline: form.tagline,
        websiteUrl: form.websiteUrl,
        displayOrder: form.displayOrder ? Number(form.displayOrder) : 0,
        isActive: form.isActive
      };

      const shouldUseUpload = imageInputMode === 'upload';
      let requestBody = payload;

      if (shouldUseUpload && imageFile) {
        const formData = new FormData();
        formData.append('__payload', JSON.stringify(payload));
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          formData.append(key, value);
        });
        formData.append('logo', imageFile);
        requestBody = formData;
      } else if (imageInputMode === 'url') {
        payload.logo = form.logoUrl;
      } else if (editingId && existingLogoUrl) {
        payload.logo = existingLogoUrl;
      }

      if (editingId) {
        await updateCompany(editingId, requestBody);
        setSuccess('Company updated successfully!');
      } else {
        await createCompany(requestBody);
        setSuccess('Company created successfully!');
      }

      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      setImageFile(null);
      setExistingLogoUrl('');
      setImageInputMode('url');
      await refresh();
      toast.success(editingId ? 'Company updated successfully!' : 'Company created successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const errorMsg = e.message || 'Save failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      await deleteCompany(id);
      setSuccess('Company deleted successfully!');
      toast.success('Company deleted successfully!');
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      const errorMsg = e.message || 'Delete failed';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  }

  async function handleToggleActive(item) {
    try {
      await updateCompany(item._id, { isActive: !item.isActive });
      await refresh();
      toast.success(`Company ${item.isActive ? 'disabled' : 'enabled'} successfully!`);
    } catch (e) {
      const errorMsg = e.message || 'Update failed';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  }

  function startEdit(item) {
    const itemLogo = item.logo || item.logoUrl || '';
    setEditingId(item._id);
    setForm({
      name: item.name || '',
      tagline: item.tagline || '',
      websiteUrl: item.websiteUrl || '',
      logoUrl: itemLogo,
      displayOrder: item.displayOrder || 0,
      isActive: item.isActive !== false
    });
    setShowForm(true);
    setImageFile(null);
    setExistingLogoUrl(itemLogo);
    setImageInputMode(itemLogo ? 'url' : 'upload');
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(false);
    setImageFile(null);
    setExistingLogoUrl('');
    setImageInputMode('url');
    setError('');
    setSuccess('');
  }

  function handleNew() {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
    setImageFile(null);
    setExistingLogoUrl('');
    setImageInputMode('url');
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Companies</h1>
              <p className="text-xs text-gray-500">Manage partner companies and tie-ups</p>
            </div>
          </div>
          {!showForm && (
            <button
              onClick={handleNew}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-2.5 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm"
            >
              <Plus className="h-5 w-5" />
              Add Company
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">{error}</span>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              <span className="font-medium">{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {showForm && (
          <div className="bg-white shadow-xl rounded-2xl p-8 mb-8 border border-gray-200">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingId ? 'Edit Company' : 'Add Company'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {editingId ? 'Update company details below' : 'Fill in the form to add a new company'}
                </p>
              </div>
              <button
                onClick={resetForm}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Close form"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Enter company name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Display Order
                  </label>
                  <input
                    type="number"
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                    value={form.displayOrder}
                    onChange={e => setForm({ ...form, displayOrder: e.target.value })}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500">Lower numbers appear first in the marquee.</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Tagline / Short Description
                </label>
                <input
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                  value={form.tagline}
                  onChange={e => setForm({ ...form, tagline: e.target.value })}
                  placeholder="Short description or partnership note"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Website URL
                </label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    className="w-full border-2 border-gray-300 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                    value={form.websiteUrl}
                    onChange={e => setForm({ ...form, websiteUrl: e.target.value })}
                    placeholder="https://company.com"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Company Logo
                </label>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="radio"
                      name="company-logo-mode"
                      value="url"
                      checked={imageInputMode === 'url'}
                      onChange={() => setImageInputMode('url')}
                    />
                    Use Logo URL
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="radio"
                      name="company-logo-mode"
                      value="upload"
                      checked={imageInputMode === 'upload'}
                      onChange={() => setImageInputMode('upload')}
                    />
                    Upload Logo
                  </label>
                </div>

                {imageInputMode === 'url' ? (
                  <input
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                    value={form.logoUrl}
                    onChange={e => setForm({ ...form, logoUrl: e.target.value })}
                    placeholder="https://company.com/logo.png"
                  />
                ) : (
                  <div className="flex flex-col gap-3">
                  <input
                    type="file"
                    accept="image/png,image/svg+xml"
                    onChange={handleImageFileChange}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                  />
                  <p className="text-xs text-gray-500">Max file size: 1MB. PNG or SVG only. Recommended aspect ratio 3:1 or 2:1.</p>
                </div>
              )}
              <p className="text-xs text-gray-500">Tip: Use a transparent PNG or clean SVG logo for best display.</p>

                {(imagePreviewUrl || form.logoUrl || existingLogoUrl) && (
                  <div className="mt-2">
                    <div className="w-full max-w-md overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                      <img
                        src={imagePreviewUrl || getImageSrc(form.logoUrl || existingLogoUrl)}
                        alt="Company logo preview"
                        className="w-full h-auto object-contain bg-white p-4"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}
                {imageInputMode === 'url' && (
                  <p className="text-xs text-gray-500">PNG or SVG only. Recommended aspect ratio 3:1 or 2:1.</p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-gray-700">Active Status</label>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, isActive: !prev.isActive }))}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    form.isActive
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-gray-50 border-gray-200 text-gray-600'
                  }`}
                >
                  {form.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  {form.isActive ? 'Visible' : 'Hidden'}
                </button>
              </div>

              <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-3 rounded-lg hover:from-primary-700 hover:to-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg font-semibold"
                >
                  <Save className="h-5 w-5" />
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Company Name</label>
              <input
                type="text"
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                placeholder="Search company name..."
                value={filters.name}
                onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Status</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="all">All</option>
                <option value="active">Visible</option>
                <option value="inactive">Hidden</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <h3 className="text-lg font-semibold text-gray-900">Companies List</h3>
            <p className="text-sm text-gray-500 mt-1">Manage visibility and ordering for the homepage marquee</p>
          </div>

          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                <p className="text-gray-600">Loading companies...</p>
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
                  <Building2 className="h-8 w-8 text-primary-600" />
                </div>
                <p className="text-gray-600 mb-2 font-medium">No companies added yet.</p>
                <p className="text-sm text-gray-500 mb-6">Add partner companies to showcase on the homepage</p>
                <button
                  onClick={handleNew}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg font-semibold"
                >
                  <Plus className="h-5 w-5" />
                  Add Your First Company
                </button>
              </div>
            ) : (
              items.map(item => (
                <div key={item._id} className="p-6 flex items-start justify-between gap-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all group">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="w-16 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden shadow-sm">
                      {item.logo ? (
                        <img
                          src={getImageSrc(item.logo)}
                          alt={`${item.name} logo`}
                          className="max-h-8 max-w-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <Building2 className="h-6 w-6 text-gray-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h4 className="font-bold text-gray-900 text-lg">{item.name}</h4>
                        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${
                          item.isActive !== false
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}>
                          {item.isActive !== false ? 'Visible' : 'Hidden'}
                        </span>
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                          Order: {item.displayOrder || 0}
                        </span>
                      </div>
                      {item.tagline && (
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2 leading-relaxed">{item.tagline}</p>
                      )}
                      {item.websiteUrl && (
                        <a
                          href={item.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {item.websiteUrl}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleToggleActive(item)}
                      className="p-3 border-2 border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm group-hover:shadow-md"
                      title={item.isActive !== false ? 'Disable' : 'Enable'}
                    >
                      {item.isActive !== false ? <EyeOff className="h-5 w-5 text-gray-600" /> : <Eye className="h-5 w-5 text-emerald-600" />}
                    </button>
                    <button
                      onClick={() => startEdit(item)}
                      className="p-3 border-2 border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-all shadow-sm group-hover:shadow-md"
                      title="Edit company"
                    >
                      <Edit2 className="h-5 w-5 text-blue-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="p-3 border-2 border-red-200 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all shadow-sm group-hover:shadow-md"
                      title="Delete company"
                    >
                      <Trash2 className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCompaniesPage;
